/**
 * =============================================================================
 * Componente: ReferralCrmIntegrationSection
 * Versão: 1.1.0 (Módulo Indicações Centralizadas — Etapa 4)
 *
 * Descrição:
 *   Seção reutilizável para configurar a integração CRM (criação automática
 *   de NEGÓCIO) em Programas e Campanhas de Indicação.
 *
 *   Replica exatamente o padrão usado em `CrmIntegrationSection`
 *   do módulo de Agendamento Online (tipos de evento) — para garantir UX
 *   consistente em todo o sistema (Workspace → Pipeline → Fase → Origem →
 *   Vendedor/Fila de distribuição).
 *
 *   IMPORTANTE: a criação de CONTATO sempre ocorre (auto_create_contact = true
 *   no payload). O toggle foi REMOVIDO da UI para impedir indicações órfãs
 *   (sem contato), o que quebraria toda a arquitetura do sistema (negócios,
 *   conversas, atividades, etc., dependem de um contato vinculado).
 *
 * RELEASE:
 *   2026-04-26 v1.2.0 - Correção: seletor "Atribuição do vendedor" agora
 *                       permite escolher Manual/Fila antes de preencher
 *                       o vendedor/fila (estado local controlado em vez
 *                       de derivado puro).
 *   2026-04-26 v1.1.0 - Adicionado crm_workspace_id no formulário (corrige bug
 *                       de seleção de Pipeline/Fase). Adicionado campo
 *                       obrigatório "Origem do lead". Removido toggle "Criar
 *                       contato no CRM" (sempre forçado para true).
 *   2026-04-26 v1.0.0 - Criação inicial.
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Briefcase, Check, ChevronsUpDown, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { LEAD_SOURCES, LEAD_SOURCE_ICONS } from '@/lib/lead-sources';
import { createLogger } from '@/lib/logger';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { PhaseSelector } from '@/components/crm/PhaseSelector';

const logger = createLogger('ReferralCrmIntegrationSection');

/** Estrutura única dos dados manipulados (subset compartilhado entre programa e campanha). */
export interface ReferralCrmFormData {
  /** Sempre true — mantido por compatibilidade com a RPC. */
  auto_create_contact: boolean;
  auto_create_deal: boolean;
  /** NOVO em v1.1.0 — necessário para o seletor de Pipeline funcionar. */
  crm_workspace_id: string | null;
  crm_pipeline_id: string | null;
  crm_phase_id: string | null;
  /** NOVO em v1.1.0 — origem do lead exibida no negócio. */
  crm_source: string | null;
  crm_default_seller_id: string | null;
  crm_distribution_queue_id: string | null;
  crm_default_tags: string[];
  crm_deal_title_template: string | null;
  /**
   * NOVO em v1.3.0 — Modo de atribuição (mutex vendedor x fila).
   * Valores: 'fixed_seller' | 'queue' | null (sem atribuição / herda do programa
   * em runtime, no caso da campanha).
   */
  crm_assignment_mode: 'fixed_seller' | 'queue' | null;
}

interface DistributionQueue {
  id: string;
  name: string;
  distribution_method: string;
}

interface Props {
  /** Dados atuais do formulário CRM. */
  form: ReferralCrmFormData;
  /** Callback para alterar campos parciais. */
  onChange: (updates: Partial<ReferralCrmFormData>) => void;
  /**
   * Se TRUE, indica que esta seção é exibida no contexto de uma CAMPANHA
   * (e não de um programa). v1.2.0 — substitui a antiga prop showInheritToggle.
   * Quando true, exibe um aviso explicando o override granular: campos
   * preenchidos na campanha sobrescrevem o programa pai; campos vazios
   * herdam automaticamente.
   */
  isCampaignContext?: boolean;
  /** @deprecated v1.2.0 — toggle removido. Mantido apenas para compatibilidade de tipos. */
  showInheritToggle?: boolean;
  /** @deprecated v1.2.0 — não usado. */
  inheritFromProgram?: boolean;
  /** @deprecated v1.2.0 — não usado. */
  onInheritChange?: (value: boolean) => void;
}

export function ReferralCrmIntegrationSection({
  form,
  onChange,
  isCampaignContext,
  showInheritToggle,
}: Props) {
  // v1.2.0 — Override granular: aceita prop nova OU legacy (compat).
  const isCampaign = isCampaignContext ?? showInheritToggle ?? false;
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // Estados dos popovers (Workspace + Pipeline)
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [pipelineOpen, setPipelineOpen] = useState(false);

  // Carrega hierarquia de workspaces > pipelines (mesmo hook usado no scheduling)
  const { workspaces } = useCrmWorkspacesHierarchy();

  // Modo de atribuição — estado local controlado (v1.2.0 / v1.3.0)
  // v1.3.0: agora também sincronizado com `crm_assignment_mode` persistido no banco
  // (campo passou a ser oficial; antes era inferido a partir de seller/queue).
  // Mapeamento UI ↔ persistência:
  //   'manual'              ↔ 'fixed_seller'
  //   'distribution_queue'  ↔ 'queue'
  //   'none'                ↔ null
  const [assignmentMode, setAssignmentMode] = useState<
    'none' | 'manual' | 'distribution_queue'
  >(() => {
    if (form.crm_assignment_mode === 'fixed_seller') return 'manual';
    if (form.crm_assignment_mode === 'queue') return 'distribution_queue';
    // Fallback retro-compatível: deriva de seller/queue caso o modo não esteja
    // preenchido (registros antigos sem crm_assignment_mode).
    if (form.crm_default_seller_id) return 'manual';
    if (form.crm_distribution_queue_id) return 'distribution_queue';
    return 'none';
  });

  // Sincroniza o modo quando o form muda externamente (ex.: abrir outra campanha
  // para edição). Prioriza crm_assignment_mode quando já persistido; caso
  // contrário, infere por seller/queue (compatibilidade com dados antigos).
  useEffect(() => {
    if (form.crm_assignment_mode === 'fixed_seller') {
      setAssignmentMode('manual');
    } else if (form.crm_assignment_mode === 'queue') {
      setAssignmentMode('distribution_queue');
    } else if (form.crm_default_seller_id) {
      setAssignmentMode('manual');
    } else if (form.crm_distribution_queue_id) {
      setAssignmentMode('distribution_queue');
    }
  }, [
    form.crm_assignment_mode,
    form.crm_default_seller_id,
    form.crm_distribution_queue_id,
  ]);

  // Filas de distribuição (apenas quando o modo for distribution_queue)
  const { data: queues = [] } = useQuery({
    queryKey: ['referral-distribution-queues-crm', tenantId],
    queryFn: async (): Promise<DistributionQueue[]> => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from('distribution_queues')
        .select('id, name, distribution_method, context_type')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .in('context_type', ['crm', 'all'])
        .order('name');
      if (error) {
        logger.error('Erro ao buscar filas de distribuição', error);
        return [];
      }
      return (data || []) as DistributionQueue[];
    },
    enabled: !!tenantId && assignmentMode === 'distribution_queue',
  });

  // Workspace selecionado (vem direto do formulário, padrão idêntico ao scheduling)
  const selectedWorkspace = workspaces.find((w) => w.id === form.crm_workspace_id);
  const pipelines = selectedWorkspace?.pipelines || [];
  const selectedPipeline = pipelines.find((p) => p.id === form.crm_pipeline_id);

  // Limpa pipeline e fase ao trocar workspace
  const handleWorkspaceChange = (workspaceId: string) => {
    if (workspaceId === form.crm_workspace_id) {
      setWorkspaceOpen(false);
      return;
    }
    onChange({
      crm_workspace_id: workspaceId,
      crm_pipeline_id: null,
      crm_phase_id: null,
    });
    setWorkspaceOpen(false);
  };

  // Limpa fase ao trocar pipeline
  const handlePipelineChange = (pipelineId: string) => {
    if (pipelineId === form.crm_pipeline_id) {
      setPipelineOpen(false);
      return;
    }
    onChange({ crm_pipeline_id: pipelineId, crm_phase_id: null });
    setPipelineOpen(false);
  };

  const handleAssignmentModeChange = (mode: string) => {
    const next = mode as 'none' | 'manual' | 'distribution_queue';
    setAssignmentMode(next);
    // v1.3.0 — sincroniza também o crm_assignment_mode persistido,
    // garantindo mutex vendedor x fila no banco.
    if (next === 'none') {
      onChange({
        crm_assignment_mode: null,
        crm_default_seller_id: null,
        crm_distribution_queue_id: null,
      });
    } else if (next === 'manual') {
      onChange({
        crm_assignment_mode: 'fixed_seller',
        crm_distribution_queue_id: null,
      });
    } else if (next === 'distribution_queue') {
      onChange({
        crm_assignment_mode: 'queue',
        crm_default_seller_id: null,
      });
    }
  };

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          Integração com CRM
        </CardTitle>
        <CardDescription>
          Crie automaticamente um negócio no CRM quando uma indicação for
          recebida por {isCampaign ? 'esta campanha' : 'este programa'}.
          O contato é sempre criado (ou reutilizado) com base no e-mail/telefone
          do lead.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* v1.2.0 — Aviso de override granular (apenas modo campanha) */}
        {isCampaign && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-foreground/80">
            💡 <strong>Override por campo:</strong> os campos preenchidos abaixo
            sobrescrevem a configuração CRM do programa pai. Campos deixados em
            branco herdam automaticamente o valor do programa.
          </div>
        )}

        {/* Configuração detalhada — sempre visível (v1.2.0) */}
        {true && (
          <>

            {/* Toggle: criar negócio */}
            <div className="flex items-center justify-between rounded-lg border border-border/50 p-4">
              <div className="flex items-start gap-3">
                <Briefcase className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-foreground">
                    Criar negócio no CRM
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Gera um deal automaticamente no pipeline selecionado abaixo.
                  </p>
                </div>
              </div>
              <Switch
                checked={form.auto_create_deal}
                onCheckedChange={(v) =>
                  onChange({
                    auto_create_deal: v,
                    // Mantém auto_create_contact sempre true por segurança
                    auto_create_contact: true,
                  })
                }
              />
            </div>

            {/* Configuração de pipeline/fase/origem/atribuição (somente se auto_create_deal) */}
            {form.auto_create_deal && (
              <div className="space-y-4 pl-1 border-l-2 border-primary/20 ml-2">
                <div className="pl-4 space-y-4">
                  {/* Linha 1: Workspace + Pipeline */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    {/* Workspace */}
                    <div className="space-y-2">
                      <Label>
                        Workspace <span className="text-destructive">*</span>
                      </Label>
                      <Popover open={workspaceOpen} onOpenChange={setWorkspaceOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={workspaceOpen}
                            className="w-full justify-between font-normal"
                          >
                            <div className="flex items-center gap-2 truncate">
                              <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span
                                className={cn(
                                  'truncate',
                                  !selectedWorkspace && 'text-muted-foreground',
                                )}
                              >
                                {selectedWorkspace?.name || 'Selecione o workspace'}
                              </span>
                            </div>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0" align="start">
                          <Command>
                            <CommandList>
                              <CommandEmpty>Nenhum workspace encontrado.</CommandEmpty>
                              <CommandGroup>
                                {workspaces.map((ws) => (
                                  <CommandItem
                                    key={ws.id}
                                    value={ws.name}
                                    onSelect={() => handleWorkspaceChange(ws.id)}
                                  >
                                    <div className="flex items-center gap-2 flex-1">
                                      <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                                      <span className="flex-1 truncate">{ws.name}</span>
                                    </div>
                                    <Check
                                      className={cn(
                                        'ml-2 h-4 w-4',
                                        form.crm_workspace_id === ws.id
                                          ? 'opacity-100'
                                          : 'opacity-0',
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Pipeline */}
                    {form.crm_workspace_id ? (
                      <div className="space-y-2">
                        <Label>
                          Pipeline <span className="text-destructive">*</span>
                        </Label>
                        <Popover open={pipelineOpen} onOpenChange={setPipelineOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={pipelineOpen}
                              className="w-full justify-between font-normal"
                            >
                              <div className="flex items-center gap-2 truncate">
                                <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                                <span
                                  className={cn(
                                    'truncate',
                                    !selectedPipeline && 'text-muted-foreground',
                                  )}
                                >
                                  {selectedPipeline?.name || 'Selecione o pipeline'}
                                </span>
                              </div>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0" align="start">
                            <Command>
                              <CommandList>
                                <CommandEmpty>Nenhum pipeline encontrado.</CommandEmpty>
                                <CommandGroup>
                                  {pipelines.map((pip) => (
                                    <CommandItem
                                      key={pip.id}
                                      value={pip.name}
                                      onSelect={() => handlePipelineChange(pip.id)}
                                    >
                                      <div className="flex items-center gap-2 flex-1">
                                        <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                                        <span className="flex-1 truncate">
                                          {pip.name}
                                        </span>
                                      </div>
                                      <Check
                                        className={cn(
                                          'ml-2 h-4 w-4',
                                          form.crm_pipeline_id === pip.id
                                            ? 'opacity-100'
                                            : 'opacity-0',
                                        )}
                                      />
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    ) : (
                      <div />
                    )}
                  </div>

                  {/* Linha 2: Fase + Origem (idêntico ao CrmIntegrationSection do scheduling) */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    {/* Fase */}
                    <div className="space-y-2">
                      <Label>
                        Fase inicial <span className="text-destructive">*</span>
                      </Label>
                      {form.crm_pipeline_id ? (
                        <>
                          <PhaseSelector
                            pipelineId={form.crm_pipeline_id}
                            value={form.crm_phase_id}
                            onSelect={(v) => onChange({ crm_phase_id: v })}
                            excludeFinalPhases={true}
                            placeholder="Selecione a fase"
                          />
                          <p className="text-xs text-muted-foreground">
                            Fases de ganho/perda são excluídas automaticamente.
                          </p>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          disabled
                          className="w-full justify-between font-normal text-muted-foreground"
                        >
                          Selecione o pipeline primeiro
                        </Button>
                      )}
                    </div>

                    {/* Origem do lead */}
                    <div className="space-y-2">
                      <Label>
                        Origem <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={form.crm_source || ''}
                        onValueChange={(v) => onChange({ crm_source: v || null })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a origem" />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_SOURCES.map((source) => {
                            const Icon = LEAD_SOURCE_ICONS[source.id];
                            return (
                              <SelectItem key={source.id} value={source.id}>
                                <div className="flex items-center gap-2">
                                  {Icon && (
                                    <Icon className="h-4 w-4 text-muted-foreground" />
                                  )}
                                  {source.label}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Linha 3: Atribuição vendedor */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Atribuição do vendedor</Label>
                      <Select
                        value={assignmentMode}
                        onValueChange={handleAssignmentModeChange}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nenhum (sem dono)</SelectItem>
                          <SelectItem value="manual">
                            Manual (selecionar vendedor)
                          </SelectItem>
                          <SelectItem value="distribution_queue">
                            Fila de distribuição
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {assignmentMode === 'manual' && (
                      <div className="space-y-2">
                        <Label>
                          Vendedor <span className="text-destructive">*</span>
                        </Label>
                        <SellerSelector
                          value={form.crm_default_seller_id}
                          onSelect={(v) => onChange({ crm_default_seller_id: v })}
                          placeholder="Selecione o vendedor"
                        />
                      </div>
                    )}

                    {assignmentMode === 'distribution_queue' && (
                      <div className="space-y-2">
                        <Label>
                          Fila de distribuição{' '}
                          <span className="text-destructive">*</span>
                        </Label>
                        <Select
                          value={form.crm_distribution_queue_id || ''}
                          onValueChange={(v) =>
                            onChange({ crm_distribution_queue_id: v || null })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a fila" />
                          </SelectTrigger>
                          <SelectContent>
                            {queues.map((queue) => (
                              <SelectItem key={queue.id} value={queue.id}>
                                {queue.name} ({queue.distribution_method})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
